import { AfterViewInit, Component, EventEmitter, Input, Output, Renderer2, ViewChild } from '@angular/core';
import {
  fieldContainerStyles,
  fieldIconsStyles,
  fieldStyles,
  filterSelectItems,
  optionsContainerStyles,
  optionsStyles,
  pillsContainerStyles,
  selectAppendStyles,
  selectContainerStyles,
  SelectProps,
} from '@kite_aerolab/shared/components';
import { getRandomID, getStringAsID } from '@kite_aerolab/shared/utils';

@Component({
  selector: 'kite-select',
  templateUrl: './select.component.html',
})
export class SelectComponent implements AfterViewInit {
  isFocused = false;
  isOpen = false;
  multipleValue: any[] = [];
  originalOptions: any;
  selected: string;
  value: string;
  isOnBottom = false;

  @ViewChild('inputElement') inputElement: any;
  @ViewChild('listElement') listElement: any;
  @ViewChild('containerElement') containerElement: any;
  /**
   *
   * Obtener los estados del textarea
   *
   * */
  /**
   * Label del campo
   */
  @Input() label: string;
  /**
   * Mensaje de ayuda
   */
  @Input() helper: string;
  /**
   * Mensaje de error
   */
  @Input() error: string;
  /**
   * CSS props del input
   */
  @Input() css: SelectProps['css'];
  /**
   * Estado invalido
   */
  @Input() isInvalid: boolean;
  /**
   * Estado invalido
   */
  @Input() isDisabled: boolean;
  /**
   * Agrega la opción de limpiar el campo
   */

  @Input() isClearable = true;
  /**
   * Opciones del selector
   */
  @Input() options: string[];
  /**
   * Aria label
   */
  @Input() ariaLabel: string;
  /**
   * Clases del componente
   */
  @Input() className: string;
  /**
   * Tipo. Puede ser default o multiple
   */
  @Input() type: string;
  /**
   * Id del componente
   */
  @Input() id: string = getRandomID();

  /**
   * Placeholder del input
   */
  @Input() placeholder: string;

  /**
   * Opción múltiple del componente
   */
  @Input() isMultiple: boolean;

  /**
   * valor por default del componente
   */
  @Input() defaultValue: string;

  /**
   * Propiedad readonly del input
   */
  @Input() isReadOnly: boolean;

  /**
   * Función que se ejecuta cuando cambia el value
   */
  @Output() onValueChange = new EventEmitter();

  constructor(private renderer: Renderer2) {
    this.renderer.listen('window', 'click', (e: Event) => {
      e.stopPropagation();
      if (!this.containerElement.nativeElement.contains(e.target)) {
        this.handleClickOutside();
      }
    });
  }

  /**
   * Estilos del select
   */
  public get fieldStyles(): string[] {
    return [
      fieldStyles({
        isInvalid: this.isInvalid,
        isDisabled: this.isDisabled,
        isFocused: this.isFocused,
        isReadOnly: this.isReadOnly,
      }).className,
    ];
  }

  public get optionsContainerStyles(): string[] {
    return [optionsContainerStyles().className];
  }

  /**
   * Estilos del select
   */
  public get optionsStyles(): string[] {
    const isScrollable = this.options ? this.options.length > 3 : false;
    return [
      optionsStyles({
        isOpen: !!this.isOpen,
        isScrollable: isScrollable,
        isOnBottom: this.isOnBottom,
      }).className,
    ];
  }

  /**
   * Estilos del contenedor
   */
  public get selectContainerStyles(): string[] {
    return [selectContainerStyles({ css: this.css }).className];
  }

  /**
   * Estilos del append
   */
  public get selectAppendStyles(): string[] {
    return [selectAppendStyles().className];
  }

  /**
   * Estilos de los iconos
   */

  public get fieldIconsStyles(): string[] {
    return [fieldIconsStyles().className];
  }

  public get fieldContainerStyles(): string[] {
    return [fieldContainerStyles().className];
  }

  public get pillsContainerStyles(): string[] {
    return [pillsContainerStyles().className];
  }

  getStringAsID(value: string) {
    return getStringAsID(value);
  }

  ngOnInit() {
    if (this.defaultValue) {
      /**
       * Chequeamos si hay un valor por default, y se lo asignamos a value si está(n) en las opciones
       */
      if (!this.isMultiple && typeof this.defaultValue === 'string' && this.options.includes(this.defaultValue)) {
        this.value = this.defaultValue;
        this.selected = this.defaultValue;
      } else if (
        this.isMultiple &&
        Array.isArray(this.defaultValue) &&
        this.defaultValue.every((option: string) => {
          return this.options.indexOf(option) !== -1;
        })
      ) {
        this.multipleValue = this.defaultValue;
        this.options = this.options.filter((option) => this.multipleValue.indexOf(option) === -1);
      }
    }
  }

  ngAfterViewInit() {
    /**
     * Guardamos las opciones originales antes de arrancar
     */
    this.originalOptions = this.options;
    /*
     * Detectamos si el select está en el borde inferior de la página y
     * le ponemos un listener on scroll para que se ajuste cuando lo esté
     */
    this.handleOptionsPosition();
    window.addEventListener('scroll', this.handleOptionsPosition.bind(this));
  }

  handleOptionsPosition() {
    const windowHeight = window.innerHeight;
    const elementPosition = this.containerElement.nativeElement.getBoundingClientRect().bottom;
    if (elementPosition >= windowHeight - 288) {
      this.isOnBottom = true;
    } else {
      this.isOnBottom = false;
    }
  }

  handleChanges(val: string) {
    this.isOpen = true;
    if (val) {
      this.value = val;
      if (this.isMultiple) {
        const filteredOptions = this.options.filter((val) => {
          return !this.multipleValue.includes(val);
        });
        this.options = filterSelectItems(filteredOptions, val) || [];
      } else {
        this.options = filterSelectItems(this.originalOptions, val) || [];
      }
    } else {
      this.options = this.originalOptions;
      if (this.isMultiple) {
        this.options = this.options.filter((val) => {
          return !this.multipleValue.includes(val);
        });
      }
    }
  }

  removeOption(val: string, event: any) {
    this.multipleValue = this.multipleValue.filter((option) => option !== val && option);
    event.stopPropagation();
    this.inputElement.nativeElement.focus();
    this.options = [...this.options, val].sort();
  }

  handleClickOutside() {
    this.isOpen = false;
    this.isFocused = false;
    if (!this.isMultiple) {
      if (!this.originalOptions.includes(this.value) || !this.selected) {
        this.value = '';
        this.selected = '';
        this.options = this.originalOptions;
      }
    }
  }

  handleFocus() {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    this.isFocused = true;
    this.inputElement.nativeElement.focus();
  }

  toggleOpen(event: any) {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    event.stopPropagation();
  }

  handleClick(val: string, el: any) {
    this.value = '';
    this.isOpen = false;

    if (this.isMultiple) {
      this.multipleValue = [...this.multipleValue, val];
      this.inputElement.nativeElement.focus();
      const filteredOptions = this.options.filter((opt) => {
        return opt !== val;
      });
      this.options = filteredOptions;
      this.onValueChange.emit([...this.multipleValue]);
    } else {
      const list = document.querySelectorAll('li');
      list.forEach((li) => {
        li.classList.remove('active');
      });
      this.value = val;
      this.selected = val;
      el.target.classList.add('active');
      this.onValueChange.emit(this.value);
    }
  }

  handleKeyPress(event: any) {
    const list = this.listElement.nativeElement;
    const selected = list.getElementsByClassName('selected')[0] || null;
    if (event.key !== 'Tab') this.isOpen = true;

    if (event.key === 'Enter') {
      if (!selected.classList.contains('empty')) {
        const selectedValue = selected.dataset.value;
        this.handleClick(selectedValue, event);
        this.handleClickOutside();
      }
    }

    if (event.key === 'ArrowDown') {
      if (selected) selected.classList.remove('selected');
      if (selected && selected.nextElementSibling) {
        selected.nextElementSibling.classList.add('selected');
        selected.nextElementSibling.focus();
      } else {
        list.firstElementChild.classList.add('selected');
        list.firstElementChild.focus();
      }
    } else if (event.key === 'ArrowUp') {
      if (selected) selected.classList.remove('selected');
      if (selected && selected.previousElementSibling && !selected.previousElementSibling.classList.contains('empty')) {
        selected.previousElementSibling.classList.add('selected');
        selected.previousElementSibling.focus();
      } else {
        list.lastElementChild.classList.add('selected');
        list.lastElementChild.focus();
      }
    } else {
      this.inputElement.nativeElement.focus();
    }
  }

  /**
   * Clear del input
   */
  clearText(event: any) {
    this.value = '';
    this.multipleValue = [];
    this.options = this.originalOptions;
    const list = document.querySelectorAll('li.active');
    list.forEach((li) => {
      li.classList.remove('active');
    });
    event.stopPropagation();
  }
}
